import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, MapPin, Clock, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyStore = {
  name: '',
  address: '',
  hours: '',
  maps_url: '',
  rappi_url: '',
  image: 'https://images.unsplash.com/photo-1755409022211-93efd6ecae73?w=800',
  active: true,
  visible_on_web: true
};

export default function AdminStores() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyStore);

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const fetchStores = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/stores`, { withCredentials: true, headers: getHeaders() });
      setStores(data);
    } catch (err) {
      console.error(err);
      toast.error('Error al cargar tiendas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStores();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyStore);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name,
      address: s.address,
      hours: s.hours,
      maps_url: s.maps_url,
      rappi_url: s.rappi_url || '',
      image: s.image || 'https://images.unsplash.com/photo-1755409022211-93efd6ecae73?w=800',
      active: s.active,
      visible_on_web: s.visible_on_web !== false
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.hours || !form.maps_url) {
      toast.error('Completa los campos obligatorios');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API}/admin/stores/${editing.id}`, form, { withCredentials: true, headers: getHeaders() });
        toast.success('Tienda actualizada');
      } else {
        await axios.post(`${API}/admin/stores`, form, { withCredentials: true, headers: getHeaders() });
        toast.success('Tienda creada');
      }
      setDialogOpen(false);
      fetchStores();
    } catch (err) {
      console.error(err);
      toast.error('Error al guardar tienda');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta tienda?')) return;
    try {
      await axios.delete(`${API}/admin/stores/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Tienda eliminada');
      fetchStores();
    } catch (err) {
      console.error(err);
      toast.error('Error al eliminar tienda');
    }
  };

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    const toastId = toast.loading('Subiendo imagen...');
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };
      const { data } = await axios.post(`${API}/admin/upload`, formData, {
        withCredentials: true,
        headers
      });

      handleChange('image', data.url);
      toast.success('Imagen subida', { id: toastId });
    } catch (err) {
      console.error(err);
      toast.error('Error al subir imagen', { id: toastId });
    }
  };

  return (
    <div data-testid="admin-stores-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#46371c]">Nuestras Tiendas</h1>
          <p className="font-body text-sm text-[#75664d] mt-1">Gestión de puntos de venta y horarios</p>
        </div>
        <Button onClick={openCreate} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="add-store-btn">
          <Plus className="w-4 h-4 mr-2" /> Agregar Tienda
        </Button>
      </div>

      <Card className="border border-[#e5dfd3] bg-[#fcfbf9] overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : stores.length === 0 ? (
          <div className="text-center py-12 font-body text-sm text-[#75664d]">
            No hay tiendas registradas. Haz clic en "Agregar Tienda" para crear una.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#f4f1ea]">
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Tienda</TableHead>
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Dirección</TableHead>
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Ubicación Maps</TableHead>
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Visible en Web</TableHead>
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Estado</TableHead>
                <TableHead className="font-body text-xs font-semibold text-[#46371c]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stores.map(s => (
                <TableRow key={s.id} data-testid={`store-row-${s.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img src={s.image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      <div>
                        <p className="font-body text-sm font-medium text-[#46371c]">{s.name}</p>
                        <p className="font-body text-xs text-[#75664d] flex items-center gap-1"><Clock className="w-3 h-3" /> {s.hours}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-body text-sm text-[#46371c]">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="w-4 h-4 text-[#75664d]" />
                      <span>{s.address}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {s.maps_url ? (
                      <a href={s.maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#a0d5ba] hover:underline font-body font-medium">
                        Ver Mapa <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    ) : (
                      <span className="text-xs text-[#75664d] font-body">No configurado</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={s.visible_on_web !== false ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'}>
                      {s.visible_on_web !== false ? 'Visible en Web' : 'Oculta (Solo Admin)'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={s.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {s.active ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(s)} className="p-2 hover:bg-[#f4f1ea] rounded-lg transition-colors" data-testid={`edit-store-${s.id}`}>
                        <Pencil className="w-4 h-4 text-[#75664d]" />
                      </button>
                      <button onClick={() => handleDelete(s.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-store-${s.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#fcfbf9]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#46371c]">{editing ? 'Editar Tienda' : 'Nueva Tienda'}</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#75664d]">Completa los datos del punto de venta</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Nombre de la Tienda *</label>
              <input value={form.name} onChange={e => handleChange('name', e.target.value)} required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="store-name-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Dirección *</label>
              <input value={form.address} onChange={e => handleChange('address', e.target.value)} required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="store-address-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Horarios de Atención *</label>
              <input value={form.hours} onChange={e => handleChange('hours', e.target.value)} placeholder="Ej: Lun-Sab: 9am-7pm | Dom: 10am-5pm" required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="store-hours-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">URL Ubicación Google Maps *</label>
              <input value={form.maps_url} onChange={e => handleChange('maps_url', e.target.value)} placeholder="https://maps.google.com/..." required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="store-maps-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">URL Tienda Rappi (Opcional)</label>
              <input value={form.rappi_url} onChange={e => handleChange('rappi_url', e.target.value)} placeholder="https://www.rappi.com.co/..." className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="store-rappi-input" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Imagen de la Tienda</label>
              <div className="flex items-center gap-4">
                {form.image && (
                  <img src={form.image} alt="" className="w-16 h-16 rounded-xl object-cover border border-[#e5dfd3]" />
                )}
                <div className="flex-1">
                  <label className="inline-flex items-center justify-center px-4 h-10 rounded-xl border border-dashed border-[#e5dfd3] hover:border-[#a0d5ba] bg-[#fcfbf9] hover:bg-[#eef6f2] cursor-pointer text-xs font-semibold text-[#75664d] hover:text-[#a0d5ba] transition-colors w-full font-body">
                    <span>{form.image ? 'Cambiar Imagen' : 'Subir Imagen'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="store-image-upload"
                    />
                  </label>
                  <p className="text-[10px] text-[#75664d] mt-1 font-body">Sube una imagen desde tu equipo (JPG, PNG, WebP)</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2 py-2 border-t border-[#e5dfd3]/50 pt-3">
              <div className="flex items-center gap-2">
                <input type="checkbox" id="store-active" checked={form.active} onChange={e => handleChange('active', e.target.checked)} className="rounded" />
                <label htmlFor="store-active" className="font-body text-sm text-[#46371c] cursor-pointer">Tienda Activa en el Sistema</label>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="store-visible-web" checked={form.visible_on_web !== false} onChange={e => handleChange('visible_on_web', e.target.checked)} className="rounded" />
                <label htmlFor="store-visible-web" className="font-body text-sm text-[#46371c] cursor-pointer">
                  Visible en la Página Web <span className="text-xs text-[#75664d]">(Desmarcar para ocultar de la web pero registrar ventas internamente)</span>
                </label>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full border-[#e5dfd3] text-[#46371c] font-body">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="save-store-btn">Guardar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
